import React from 'react';
import { LinearProgress } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * GradientProgress - A customizable progress bar component with gradient colors based on progress value
 *
 * @param {Object} props
 * @param {number} props.value - The progress value (0-100)
 * @param {number} [props.height=8] - Height of the progress bar in pixels
 * @param {Object} [props.thresholds] - Custom threshold values for color transitions
 * @param {Object} [props.colors] - Custom colors for different progress states
 * @param {Object} [props.sx] - Additional MUI sx styles
 */
const GradientProgress = ({
  value,
  height = 8,
  thresholds = { low: 33, medium: 70 },
  colors,
  sx = {},
}) => {
  // Ensure value is between 0 and 100
  const normalizedValue = Math.min(Math.max(value, 0), 100);

  // Default color scheme if not provided
  const defaultColors = {
    error: '#f44336', // Red
    warning: '#ffa726', // Orange
    success: '#66bb6a', // Green
  };

  const progressColors = {
    error: colors?.error || defaultColors.error,
    warning: colors?.warning || defaultColors.warning,
    success: colors?.success || defaultColors.success,
  };

  const getGradientBackground = (theme) => {
    if (normalizedValue < thresholds.low) {
      return `linear-gradient(90deg, ${progressColors.error} ${
        100 - normalizedValue
      }%, ${progressColors.error} 100%)`;
    } else if (normalizedValue < thresholds.medium) {
      return `linear-gradient(90deg, ${progressColors.error} ${
        100 - normalizedValue
      }%, ${progressColors.warning} 100%)`;
    } else {
      return `linear-gradient(90deg, ${progressColors.error} ${
        100 - normalizedValue
      }%, ${progressColors.warning} ${thresholds.medium}%, ${
        progressColors.success
      } 100%)`;
    }
  };

  return (
    <LinearProgress
      variant="determinate"
      value={normalizedValue}
      sx={{
        width: '100%',
        height,
        borderRadius: height / 2,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.12)'
            : 'rgba(0, 0, 0, 0.12)',
        '& .MuiLinearProgress-bar': {
          borderRadius: height / 2,
          backgroundImage: getGradientBackground,
          transition: 'transform .4s linear',
        },
        ...sx,
      }}
    />
  );
};

GradientProgress.propTypes = {
  value: PropTypes.number.isRequired,
  height: PropTypes.number,
  thresholds: PropTypes.shape({
    low: PropTypes.number,
    medium: PropTypes.number,
  }),
  colors: PropTypes.shape({
    error: PropTypes.string,
    warning: PropTypes.string,
    success: PropTypes.string,
  }),
  sx: PropTypes.object,
};

export default GradientProgress;
